'use client';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Icon } from '@ui/components';
import { useClickOutside } from '../clicks/use-click-outside';
import dynamic from 'next/dynamic';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface MediaPreview {
  src: string;
  isVideo: boolean;
}

interface ImagePreviewContextProps {
  setImagePreview: (src: string) => void;
  setMediaPreview: (src: string, isVideo: boolean) => void;
  closePreview: () => void;
}

const ImagePreviewContext = createContext<ImagePreviewContextProps | undefined>(undefined);

export const useImagePreviewContext = (): ImagePreviewContextProps => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error('useImagePreviewContext must be used within an ImagePreviewProvider');
  }
  return context;
};

interface ImagePreviewProviderProps {
  children: ReactNode;
}

export const ImagePreviewProvider: React.FC<ImagePreviewProviderProps> = ({ children }) => {
  const [media, setMedia] = useState<MediaPreview | null>(null);

  const setImagePreview = useCallback((src: string) => {
    setMedia({ src, isVideo: false });
  }, []);

  const setMediaPreview = useCallback((src: string, isVideo: boolean) => {
    setMedia({ src, isVideo });
  }, []);

  const closePreview = useCallback(() => setMedia(null), []);
  const ref = useClickOutside<HTMLDivElement>(() => closePreview());

  useEffect(() => {
    if (!media) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [media, closePreview]);

  return (
    <ImagePreviewContext.Provider value={{ setImagePreview, setMediaPreview, closePreview }}>
      {children}
      <AnimatePresence>
        {media && (
          <motion.div
            key="preview"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            style={{ margin: 0, zIndex: 1000 }}>
            <div
              ref={ref}
              className="max-h-[90vh] max-w-[90vw] w-full flex items-center justify-center">
              {media.isVideo ? (
                <div className="relative w-full max-w-4xl" style={{ aspectRatio: '16/9' }}>
                  <ReactPlayer
                    url={media.src}
                    controls
                    playing
                    width="100%"
                    height="100%"
                    style={{ position: 'absolute', top: 0, left: 0 }}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload',
                          playsInline: true,
                        },
                      },
                    }}
                  />
                </div>
              ) : (
                <img
                  src={media.src}
                  alt="Preview"
                  className="max-h-full max-w-full object-contain"
                />
              )}
            </div>

            <Button type="icon" className="absolute right-4 top-4 z-10" onClick={closePreview}>
              <Icon type="close" />
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </ImagePreviewContext.Provider>
  );
};
